
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions, ColumnEditorOptions, ColumnEvent } from 'primereact/column';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { useRef, useState } from 'react';
import { InputNumber } from 'primereact/inputnumber';
import { confirmDialog } from 'primereact/confirmdialog'; // For confirmDialog method
import { useNavigate } from "react-router-dom";

// import {
// 	atan2, chain, derivative, e, evaluate, log, pi, pow, round, sqrt
//   } from 'mathjs'

type LPoint = {
	x: number,
	y: number,
}
function LemniscatesPage() {
	const [plotX, setPlotX] = useState<number|null>(null);
	const [plotY, setPlotY] = useState<number|null>(null);
	const [plotDX, setPlotDX] = useState<number|null>(null);
	const [plotDY, setPlotDY] = useState<number|null>(null);
	const [searchDepth, setSearchDepth] = useState<number|null>(null);
	const [plotDepth, setPlotDepth] = useState<number|null>(null);
	const [points, setPoints] = useState<LPoint[]>([{ "x": -1, "y": 0}, { "x": 1, "y": 0}]);
	const drawCanvas = useRef<HTMLCanvasElement>(null);
	const drawContainer = useRef<HTMLDivElement>(null);
	const navigate = useNavigate();

	let stop_plot = false;
	let zoom = 1;
	let plot_x = 0;
	let plot_y = 0;
	let plot_dx = 0;
	let plot_dy = 0;
	let search_depth = 0;
	let plot_depth = 0;

	const confirmDeletePoint = (rowData:LPoint, options: ColumnBodyOptions) =>
	{
        confirmDialog({
            message: `Are you sure you want to delete point (${rowData.x},${rowData.y})?`,
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
				const _points = [...points];
				_points.splice(options.rowIndex, 1);
				setPoints(_points);	
			}
        });
	}

	const actionBodyTemplate = (rowData: any, options: ColumnBodyOptions) => {
        return (
            <>
                {/* <Button icon="pi pi-pencil" rounded outlined className="mr-2" onClick={() => editProduct(rowData)} /> */}
                <Button icon="pi pi-trash" rounded outlined severity="danger" size="small" 
					style={{padding: '0', height: '1.6rem', width: '1.6rem'}}
					onClick={() => confirmDeletePoint(rowData, options)} />
            </>
        );
    };
	function newPoint() 
	{
		const _points = [...points];
		_points.push({x:0,y:0});
		setPoints(_points);
	}
	const onCellEditComplete = (e:ColumnEvent) => {
        let { rowData, newValue, field, originalEvent: event } = e;
		
		if (newValue !== null) 
			rowData[field] = newValue;
		else 
			event.preventDefault();
    };
    const numberEditor = (options: ColumnEditorOptions) => {
        return <InputNumber value={options.value} onValueChange={(e) => options.editorCallback!(e.value)} 
				useGrouping={false} maxFractionDigits={5} size={6}
				mode="decimal" showButtons buttonLayout="horizontal" step={0.1} 
				inputStyle={{padding:'0.1rem 0.2rem'}}
				decrementButtonClassName='p-2'
				incrementButtonClassName='p-2' />;
    };

	function eval_function(x:number, y:number) 
	{
		var distance = 1.0;
		points.forEach(p => {
			distance *= Math.sqrt((x - p.x) * (x - p.x) + (y - p.y) * (y - p.y));
		});
		return distance - 1;
	}
	function subdivide(imageData: ImageData, depth:number , x:number, y:number, dx:number, dy:number)
	{
		create_tree(imageData, depth + 1, x, y, dx / 2, dy / 2);
		create_tree(imageData, depth + 1, x + dx / 2, y, dx / 2, dy / 2);
		create_tree(imageData, depth + 1, x + dx / 2, y + dy / 2, dx / 2, dy / 2);
		create_tree(imageData, depth + 1, x, y + dy / 2, dx / 2, dy / 2);
	}
	function contour_present(x:number, y:number, dx:number, dy:number)
	{
		var s1 = eval_function(x, y);
		var s2 = eval_function(x + dx, y);
		var s3 = eval_function(x, y + dy);
		var s4 = eval_function(x + dx, y + dy);
		return !(Math.sign(s1) === Math.sign(s2) && Math.sign(s2) === Math.sign(s3) 
				&& Math.sign(s3) === Math.sign(s4));
	}
	function setPixel(imageData: ImageData, x:number, y:number, r:number, g:number, b:number) 
	{
		var index = (Math.round(x) + Math.round(y) * imageData.width) * 4;
		imageData.data[index+0] = r;
		imageData.data[index+1] = g;
		imageData.data[index+2] = b;
		imageData.data[index+3] = 255;
	}
	function create_tree(imageData: ImageData, depth:number, x:number, y:number, dx:number, dy:number)
	{
		if (stop_plot)
			return;

		if (depth < search_depth)
		{
			subdivide(imageData, depth, x, y, dx, dy);
		}
		else if (contour_present(x, y, dx, dy))
		{
			if ( depth < plot_depth)
				subdivide(imageData, depth, x, y, dx, dy);
			else
			{
				var px = (x + dx / 2 - plot_x)*zoom;
				var py = (plot_dy - (y + dy / 2) + plot_y)*zoom;
				setPixel(imageData, px, py, 0, 0, 0);
				setPixel(imageData, px, py + 1, 0, 0, 0);
				setPixel(imageData, px + 1, py, 0, 0, 0);
				setPixel(imageData, px + 1, py + 1, 0, 0, 0);
			}
		}
	}

	function onRefresh()
	{
		if ( points.length === 0 )
        	return;
		if (!drawCanvas.current)
			return;
		if (!drawContainer.current)
			return;
		
		if ( plotX===null || plotY===null || plotDX===null || plotDY===null ||
			 plotX===0 || plotY===0 || plotDX===0 || plotDY===0 )
		{
			plot_x = Number.MAX_VALUE;
			plot_y = Number.MAX_VALUE;
			let x = Number.MIN_VALUE;
			let y = Number.MIN_VALUE;
			points.forEach(element => {
				plot_x = Math.min(plot_x, element.x);
				plot_y = Math.min(plot_y, element.y);
				x = Math.max(x, element.x);
				y = Math.max(y, element.y);
			});
			plot_x -= 1;
			plot_y -= 1;
			x += 1;
			y += 1;
			plot_dx = x - plot_x;
			plot_dy = y - plot_y;
		}
		else
		{
			plot_x = plotX;
			plot_y = plotY;
			plot_dx = plotDX;
			plot_dy = plotDY;
		}
		if ( searchDepth===null || searchDepth===0 )
			search_depth = 6;
		else
			search_depth = searchDepth;
		if (plotDepth===null || plotDepth===0 )
			plot_depth = 12;
		else
			plot_depth = plotDepth;

		if ( plot_dx===0 || plot_dy===0)
			return;
		stop_plot = false;
		const ctx = drawCanvas.current.getContext("2d");
		if (!ctx) return;
		if (window.innerWidth > 1000 ) 
			drawCanvas.current.width = drawCanvas.current.height = Math.min(drawContainer.current.clientWidth, drawContainer.current.clientHeight);  
		else
		{
			drawContainer.current.style.height = drawContainer.current.clientWidth + 'px';
			drawContainer.current.style.minHeight = drawContainer.current.style.height;
			drawCanvas.current.width = drawCanvas.current.height = drawContainer.current.clientWidth;
		}
		
		const canvasWidth = drawCanvas.current.width;
		const canvasHeight = drawCanvas.current.height;

		zoom = Math.min(canvasWidth/plot_dx, canvasHeight/plot_dy);
		ctx.clearRect(0, 0, canvasWidth, canvasHeight);
		const imgData = ctx.getImageData(0, 0, canvasWidth, canvasHeight);
		create_tree(imgData, 0, plot_x, plot_y, plot_dx, plot_dy);
		ctx.putImageData(imgData, 0, 0);
	}

	return (
	<div className='lemniscates_content'>
		<div className='flex flex-row align-items-center'>
			<i className="pi pi-times-circle mr-2" style={{ fontSize: '1.5rem', cursor: 'pointer' }}
				onClick={() => navigate("/")}
			></i>
			<div>A software for drawing <a href='https://mathcurve.com/courbes2d.gb/cassinienne/cassinienne.shtml' target='_blank' rel="noreferrer"><i>polynomial lemniscates</i></a>, i.e. the loci of points such that the product of their distances to n given points (foci) equals one.<br/>Use "Add point" to add the coordinates of a focus, and "Refresh" to draw the curve.</div>
		</div>
		<div className='lemniscates_draw'>
			<div className='lemniscates_data'>
				<div className='flex flex-row mt-3 pb-1'>
					<span className='flex-grow-1'><b>Points</b></span>
					<Button label="Add point" icon="pi pi-plus" size='small' severity="success" onClick={newPoint} style={{padding: '0.2rem 0.5rem'}}/>
				</div>
				<div className='lemniscates_table' >
				<DataTable value={points} editMode="cell"size='small' stripedRows  scrollable scrollHeight='100%' style={{height:'100%'}}>
					<Column field="x" header="X" align='center' style={{ minWidth: '5rem', textAlign:'center' }} editor={(options) => numberEditor(options)} onCellEditComplete={onCellEditComplete}></Column>
					<Column field="y" header="Y" align='center' style={{ minWidth: '5rem', textAlign:'center' }} editor={(options) => numberEditor(options)} onCellEditComplete={onCellEditComplete}></Column>
					<Column body={actionBodyTemplate}></Column>
				</DataTable>
				</div>
				<Button style={{minHeight:'2rem', width:'fit-content', alignSelf:'center', margin:'.5rem 0'}} onClick={onRefresh}>Refresh</Button>
				<Panel header="Advanced" toggleable collapsed={true} style={{minWidth:'17rem'}}>
					<table>
					<tr><td >X</td><td><InputNumber value={plotX} onValueChange={(e) => setPlotX(e.value??null)} useGrouping={false} maxFractionDigits={5} mode='decimal' size={10}/></td></tr>
					<tr><td >Y</td><td><InputNumber value={plotY} onValueChange={(e) => setPlotY(e.value??null)} useGrouping={false} maxFractionDigits={5} mode='decimal' size={10}/></td></tr>
					<tr><td >DX</td><td><InputNumber value={plotDX} onValueChange={(e) => setPlotDX(e.value??null)} useGrouping={false} maxFractionDigits={5} mode='decimal' size={10}/></td></tr>
					<tr><td >DY</td><td><InputNumber value={plotDY} onValueChange={(e) => setPlotDY(e.value??null)} useGrouping={false} maxFractionDigits={5} mode='decimal' size={10}/></td></tr>
					<tr><td >Search Depth</td><td><InputNumber value={searchDepth} onValueChange={(e) => setSearchDepth(e.value??null)} useGrouping={false} maxFractionDigits={5} mode='decimal' size={10}/></td></tr>
					<tr><td >Plot Depth</td><td><InputNumber value={plotDepth} onValueChange={(e) => setPlotDepth(e.value??null)} useGrouping={false} maxFractionDigits={5} mode='decimal' size={10}/></td></tr>
					</table>
				</Panel>		
			</div>
			<div className="flex-grow-1 overflow-hidden flex justify-content-center" ref={drawContainer}>
				<canvas ref={drawCanvas} width="500" height="500" ></canvas>
			</div>
		</div>
	</div>   
	);
}

export default LemniscatesPage;


